// Models
import {
  CreateChargeIntentRequestBody,
  PaymentDetailsPayPalCommerceRequest,
  PaymentDetailsACHRequest,
  PaymentMethod,
  PaymentProcessor,
  TransactionType,
  DonateResponse,
} from 'models/ChargeIntent'

import {
  ACHFinalizationData,
  PlaidFinalizationData,
  StripeFinalizationData,
} from 'modules/order/state/payment-details/payment-details-types'
import { Transaction } from 'models/Transaction'

// Redux services
import { createChargeIntent } from 'redux/services/chargeIntent'
import paypalApi from 'redux/services/paypal'
import { donate, recurring } from 'redux/services/donate'
import { getApplication } from 'redux/services/campaign/legacy'

// Local
import { RootState, TypedDispatch } from 'redux/store'
import getStripePromise from 'utils/functions/getStripePromise'
import { FinalizeOrderArgs } from '../types'
import { getUTCDateByFormat } from 'utils/functions/dateUtils'
import { getStripeErrorParts } from '../shared/handleStripeErrorMessage'
import { StructuredError } from 'types/error'

const finalizePayment = async (
  store: RootState,
  dispatch: TypedDispatch<RootState>,
  transaction: Transaction,
  transactionToken: string,
  args: FinalizeOrderArgs,
): Promise<void> => {
  const paymentDetails = store.order.paymentDetails

  const partialBody = {
    currency: args.currency,
    amount: store.order.dcf.value.totalAmount,
    firstName: store.order.contact.firstName,
    lastName: store.order.contact.lastName,
    email: store.order.contact.email,
    address1: store.order.address.value.line1,
    city: store.order.address.value.city,
    state: store.order.address.value.state,
    country: store.order.address.value.country,
    zip: store.order.address.value.zipCode,
    ...(store.order.doubleTheDonation.value && {
      metaData: {
        employer_match: {
          employer_id: String(store.order.doubleTheDonation.value.companyId),
          employer_name: store.order.doubleTheDonation.value.companyName,
          employer_search_text: store.order.doubleTheDonation.value.companyName,
        },
      },
    }),
    // todo: revisit to understand why transaction id is undefined
    referenceId: transaction.id?.toString(),
    reCaptchaToken: args.reCaptchaToken ?? null,
  }

  let donateResponse: DonateResponse | undefined

  const isRecurring = !!args?.frequency && args.frequency.toLowerCase() != 'one-time'

  switch (paymentDetails.processor) {
    case PaymentProcessor.STRIPE: {
      const application = await dispatch(
        getApplication.initiate(String(args.campaign.campaign_id)),
      ).unwrap()

      if (paymentDetails.type === PaymentMethod.Plaid) {
        // convert paymentDetails.method from PlaidFinalizationData to StripeFinalizationData
        const plaidPaymentDetails = paymentDetails.method as PlaidFinalizationData

        const newPaymentDetails = {
          accountId: plaidPaymentDetails.accountId,
          source: plaidPaymentDetails.publicToken,
        }

        const body = Object.assign(
          {
            applicationId: application.id,
            type: TransactionType.Donation,

            paymentMethod: PaymentMethod.Stripe,
            paymentDetails: newPaymentDetails,
          },
          partialBody,
        )

        if (isRecurring) {
          const recurringBody = Object.assign(
            {
              recurringUnit: args.frequency?.toUpperCase(),
              recurUntil: args.recurringEndDate,
              recurringStartDate: getUTCDateByFormat('YYYY-MM-DD'),
              recurringTimeZone: 'Etc/UTC',
            },
            body,
          )

          donateResponse = await dispatch(
            recurring.initiate({
              campaignId: String(args.campaign.campaign_id),
              paymentMethod: paymentDetails.type,
              body: recurringBody,
              transactionToken,
            }),
          ).unwrap()
        } else {
          donateResponse = await dispatch(
            donate.initiate({
              campaignId: String(args.campaign.campaign_id),
              paymentMethod: paymentDetails.type,
              body,
              transactionToken,
            }),
          ).unwrap()
        }

        const { errors } = donateResponse

        if (errors) {
          throw new Error('An error has occurred while trying to finalize your payment')
        }

        break
      }

      // If not Plaid, then its Stripe CC or ACH account and routing
      const paymentMethod =
        paymentDetails.type === PaymentMethod.ACH
          ? (paymentDetails.method as ACHFinalizationData)
          : (paymentDetails.method as StripeFinalizationData)

      const body: CreateChargeIntentRequestBody = Object.assign(
        {
          applicationId: application.id,
          type: TransactionType.Donation,
          paymentMethod:
            paymentDetails.type === PaymentMethod.ACH ? PaymentMethod.ACH : PaymentMethod.Stripe,
          paymentDetails:
            paymentDetails.type === PaymentMethod.ACH
              ? (paymentMethod as PaymentDetailsACHRequest)
              : { source: paymentMethod?.id },
        },
        partialBody,
      )

      if (isRecurring) {
        body.recurringDetails = {
          recurringUnit: args.frequency?.toUpperCase(),
          recurUntil: args.recurringEndDate,
          recurringStartDate: getUTCDateByFormat('YYYY-MM-DD'),
          recurringTimeZone: 'Etc/UTC',
        }
      }

      const createChargeIntentResponse = await dispatch(
        createChargeIntent.initiate({
          campaignId: String(args.campaign.campaign_id),
          paymentMethod:
            paymentDetails.type === PaymentMethod.ACH ? PaymentMethod.ACH : PaymentMethod.Stripe,
          body,
          transactionToken,
        }),
      ).unwrap()

      // Manage createChargeIntentResponse errors
      if (createChargeIntentResponse?.errors) {
        switch (createChargeIntentResponse.errors.processorDeclineCode) {
          case 'email_invalid':
            // This error should be rare, as email format is validated by the form.
            throw new Error('Please check your email address.')
          case 'account_number_invalid':
            throw new Error('Please provide valid ACH details.')
          default:
            throw new Error('An error has occurred while trying to finalize your payment')
        }
      }

      const stripe = await getStripePromise(store.config.stripePublishableKey)

      // Sanity check on stripe
      if (!stripe) throw new Error('Could not finalize payment. Unable to initialize Stripe.')

      const response = await stripe.confirmPayment({
        clientSecret: createChargeIntentResponse.chargeIntent.client_secret,
        redirect: 'if_required',
      })

      const { error } = response

      if (error) {
        const stripeError = getStripeErrorParts(error.decline_code || error.code)
        throw new StructuredError(stripeError.title, stripeError.message)
      }

      break
    }

    case PaymentProcessor.PAYPAL_COMMERCE: {
      const paypalTokenData = await dispatch(
        paypalApi.endpoints.getPaypalToken.initiate({
          campaignId: String(args.campaign.campaign_id),
          currency: args.currency,
        }),
      ).unwrap()

      const body = Object.assign(
        {
          applicationId: paypalTokenData.applicationId,
          type: TransactionType.Donation,

          paymentMethod: paymentDetails.type, //PPC or Venmo
          paymentDetails: paymentDetails.method as PaymentDetailsPayPalCommerceRequest, // 'orderId' XOR 'agreementId'
        },
        partialBody,
      )

      let response

      if (isRecurring) {
        const recurringBody = Object.assign(
          {
            recurringUnit: args.frequency?.toUpperCase(),
            recurUntil: args.recurringEndDate,
            recurringStartDate: getUTCDateByFormat('YYYY-MM-DD'),
            recurringTimeZone: 'Etc/UTC',
          },
          body,
        )

        response = await dispatch(
          recurring.initiate({
            campaignId: String(args.campaign.campaign_id),
            paymentMethod: paymentDetails.type,
            body: recurringBody,
            transactionToken,
          }),
        ).unwrap()
      } else {
        response = await dispatch(
          donate.initiate({
            campaignId: String(args.campaign.campaign_id),
            paymentMethod: paymentDetails.type,
            body,
            transactionToken,
          }),
        ).unwrap()
      }

      const { errors, message } = response

      if (errors) {
        throw new Error(message)
      }

      break
    }

    default:
      throw new Error(`Unsupported payment method / processor: ${paymentDetails.processor}`)
  }
}

export default finalizePayment
